import { Component, Inject, OnInit, ViewChild } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatGridListModule } from '@angular/material/grid-list';
import { MatIconModule } from '@angular/material/icon';
import { MatSelectModule } from '@angular/material/select';
import { TranslateModule } from '@ngx-translate/core';
import { FormArray, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { MatInputModule } from '@angular/material/input';
import { StreamingComponent } from '../streaming/streaming.component';
import { TimingComponentsGroup } from '../timingComponentsGroup/timingComponentsGroup.component';
import { IDevice } from '../../models/be/device';
import { eventTypeToData, EventTypeCodes, INotificationData } from '../../models/be/notification';
import { getTimeLeft } from '../../utils/transformation.util';
import { RingtoneService } from '../../../core/services/utils/ringtone.service';
import { IonButton, IonSpinner } from "@ionic/angular/standalone";
import { catchError, concatMap, finalize, from, Observable, of, skip, switchMap, tap, throwError } from 'rxjs';
import { SharedService } from '../../../core/services/shared.service';
import { AlertsService } from '../../../core/services/alerts.service';
import { SensorService } from '../../../core/services/sensors.service';
import { ToastService } from '../../../core/services/utils/toast.service';
import { IonicColors } from '../../enums/ionicColors.enum';
import { HardwareService } from '../../../core/services/hardware.service';
import { isActualTimeInSlots } from '../../utils/check.utils';
import { IAdvancedDevicesSettings, ISimpleDevicesSettings } from '../../models/be/devicesSettings';
import { MatProgressSpinnerModule } from "@angular/material/progress-spinner";
import { PostureStatisticsComponent } from '../posture-statistics/posture-statistics.component';
import { SensorConnectivityComponent } from '../sensor-connectivity/sensor-connectivity.component';
import { canViewSensorConnectivity } from '../../models/be/sensor-connectivity';
import { RequestsService } from '../../../core/services/utils/requests.service';

@Component({
  selector: 'app-dialog',
  templateUrl: './room-details-dialog.component.html',
  styleUrls: ['./room-details-dialog.component.scss'],
  imports: [IonSpinner,
    IonButton,
    CommonModule,
    MatDialogModule,
    MatButtonModule,
    MatIconModule,
    MatGridListModule,
    MatFormFieldModule,
    MatSelectModule,
    TranslateModule,
    ReactiveFormsModule,
    MatInputModule,
    TimingComponentsGroup,
    StreamingComponent, MatProgressSpinnerModule, PostureStatisticsComponent, SensorConnectivityComponent],
  providers: [AlertsService]
})
export class RoomDetailsDialogComponent implements OnInit {
  private readonly alertThemeBySeverity: Record<string, {
    accent: string;
    border: string;
    surface: string;
    iconSurface: string;
    titleColor: string;
    button: string;
  }> = {
      high: {
        accent: '#ee867d',
        border: 'rgba(238, 134, 125, 0.72)',
        surface: '#fff4f1',
        iconSurface: '#ffe5e0',
        titleColor: '#7f4a42',
        button: '#f28f7f'
      },
      medium: {
        accent: '#d79b3a',
        border: 'rgba(231, 191, 125, 0.78)',
        surface: '#fff8ec',
        iconSurface: '#ffefcf',
        titleColor: '#86612b',
        button: '#dba24a'
      },
      low: {
        accent: '#b29120',
        border: 'rgba(231, 218, 83, 0.88)',
        surface: '#fffde9',
        iconSurface: '#fff5bf',
        titleColor: '#77631d',
        button: '#b99224'
      },
      offline: {
        accent: '#8b95a7',
        border: 'rgba(139, 149, 167, 0.45)',
        surface: '#f6f8fb',
        iconSurface: '#e8edf5',
        titleColor: '#5f6b7c',
        button: '#7d8899'
      },
      online: {
        accent: '#2f9e72',
        border: 'rgba(47, 158, 114, 0.4)',
        surface: '#effaf4',
        iconSurface: '#dbf5e7',
        titleColor: '#2f6c54',
        button: '#39a877'
      }
    };

  /**
   * @description FormGroup che regola tutti i campi della form nel component 
   */
  public formGroup!: FormGroup;
  public formArray!: FormArray;
  public hasError: boolean = false;
  public preferencesMode: boolean = false;
  public statisticsMode = false;
  public statisticsTab: 'postures' | 'connectivity' = 'postures';
  public selectedConnectivityDeviceId = '';
  @ViewChild(StreamingComponent) private streaming?: StreamingComponent;
  public manageRunning: boolean = false;
  public deviceSettingsMode: 'simple' | 'advanced' = 'simple';
  public eventTypeToTranslation = {
    "01": 'GENERAL.EVENTS_LIST.FALL',
    "02": 'GENERAL.EVENTS_LIST.ROOM_IN',
    "03": 'GENERAL.EVENTS_LIST.ROOM_OUT',
    "04": 'GENERAL.EVENTS_LIST.BED_IN',
    "05": 'GENERAL.EVENTS_LIST.BED_OUT',
    "06": 'GENERAL.EVENTS_LIST.DEVICE_OFFLINE',
    "07": 'GENERAL.EVENTS_LIST.DEVICE_ONLINE',
    "08": 'GENERAL.EVENTS_LIST.TEST',
    "10": 'GENERAL.EVENTS_LIST.BATHROOM_IN'
  }

  public deviceSettings?: ISimpleDevicesSettings | IAdvancedDevicesSettings;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: {
      title: string,
      beds: { id: string, name: string }[],
      roomId: string,
      devices: IDevice[],
      tutorialMode?: boolean,
      alert?: INotificationData,
      alertService: AlertsService,
      sensorService: SensorService
    },
    private ringtoneService: RingtoneService,
    public dialogRef: MatDialogRef<RoomDetailsDialogComponent>,
    private sharedSrv: SharedService,
    private toastService: ToastService,
    private hardwareSrv: HardwareService,
    private requestsService: RequestsService = null!
  ) { }

  public isMobile: boolean = this.hardwareSrv.isMobile();
  public get canViewBedStates(): boolean { return this.sharedSrv.canViewBedStates; }
  public get canViewPostureStatistics(): boolean { return this.canViewBedStates && !this.data.tutorialMode; }
  public get canViewConnectivityStatistics(): boolean {
    return !this.data.tutorialMode && canViewSensorConnectivity(this.requestsService?.getCurrentUser());
  }
  public get canViewStatistics(): boolean { return this.canViewPostureStatistics || this.canViewConnectivityStatistics; }
  public get connectivityDevices(): IDevice[] {
    return (this.data.devices || []).filter(device => !!device.id && (!device.facilities?.room?.id || device.facilities.room.id === this.data.roomId));
  }
  public get connectivityDevice(): IDevice | undefined {
    return this.connectivityDevices.find(device => device.id === this.selectedConnectivityDeviceId) || this.connectivityDevices[0];
  }

  public get statisticsBuildingId(): string | undefined {
    const unit = this.sharedSrv.unitsTree?.find(unit => unit.rooms?.some(room => room.id === this.data.roomId));
    if (unit?.buildingId) return unit.buildingId;
    const buildings = new Set((this.data.devices || [])
      .filter(device => !device.facilities?.room?.id || device.facilities.room.id === this.data.roomId)
      .map(device => device.facilities?.building?.id).filter((id): id is string => !!id));
    if (buildings.size === 1) return buildings.values().next().value;
    return this.data.alert?.facilities?.room?.id === this.data.roomId ? this.data.alert.facilities.building.id : undefined;
  }

  public toggleStatistics(): void {
    if (!this.canViewStatistics) return;
    this.preferencesMode = false;
    if (!this.statisticsMode) {
      const current = this.data.devices?.[this.streaming?.currentSensorIndex ?? 0];
      this.selectedConnectivityDeviceId = this.connectivityDevices.find(device => device.id === current?.id)?.id || this.connectivityDevices[0]?.id || '';
      if (!this.canViewPostureStatistics) this.statisticsTab = 'connectivity';
    }
    this.statisticsMode = !this.statisticsMode;
  }

  public togglePreferences(): void {
    this.statisticsMode = false;
    this.preferencesMode = !this.preferencesMode;
  }

  getTimeLeft(data: Date | string) {
    return getTimeLeft(data)
  }

  getAlertVisual(alert: INotificationData) {
    const alertMeta = eventTypeToData[alert.eventType as EventTypeCodes];
    const severityKey = (alertMeta?.severity || alert.severity || 'medium') as string;
    const theme = this.alertThemeBySeverity[severityKey] || this.alertThemeBySeverity['medium'];

    return {
      ...theme,
      icon: alertMeta?.svgIcon || alertMeta?.statusIcon || 'warning',
      useSvgIcon: !!alertMeta?.svgIcon
    };
  }

  ngOnInit() {
    this.data.sensorService.getDeviceSettingsMode$(this.data.roomId).pipe(
      switchMap((settingsMode) => {
        this.deviceSettingsMode = settingsMode.currentMode;
        if (settingsMode.currentMode === 'simple') {
          return this.data.sensorService.getSimpleDevicesSettings$(this.data.roomId).pipe(
            tap((settingsData) => {
              if (!settingsMode.hasSettings) {
                settingsData.settings.schedules = [{ start_time: "00:00", end_time: "00:00" }]
              }
            })
          );
        } else if (settingsMode.currentMode === 'advanced') {
          return this.data.sensorService.getAdvancedDevicesSettings$(this.data.roomId).pipe(
            tap((settingsData) => {
              if (!settingsMode.hasSettings) {
                settingsData.settings.fall.schedules = [{ start_time: "00:00", end_time: "00:00" }];
                settingsData.settings.room_events.schedules = [{ start_time: "00:00", end_time: "00:00" }];
                settingsData.beds.forEach((bed) => {
                  if (settingsData.settings.bed_events[bed.bedId]) {
                    settingsData.settings.bed_events[bed.bedId].schedules = [{ start_time: "00:00", end_time: "00:00" }];
                  }
                })
              }
            })
          );
        } else {
          return of(undefined);
        }
      }),
      tap((data) => {
        this.deviceSettings = data;
      }),
      catchError((err) => {
        this.toastService.showToast("Errore nel recupero dati temporizzazione", IonicColors.RED)
        throw err;
      })
    ).subscribe();
  }

  manageEvent(alert: INotificationData) {
    this.manageRunning = true;
    this.ringtoneService.stopRingtone();
    let shouldClearCurrentAlert = false;
    const alertsToManage = this.getAlertsToManage(alert);

    from(alertsToManage).pipe(
      concatMap((alertEl) =>
        this.data.alertService.manageAlert$(alertEl.id).pipe(
          tap(() => {
            this.sharedSrv.removeLocallyManagedAlert(alertEl);
            shouldClearCurrentAlert = true;
          }),
          catchError((err) => {
            if (this.isAlreadyManagedError(err)) {
              this.sharedSrv.removeLocallyManagedAlert(alertEl);
              shouldClearCurrentAlert = true;
              return of(null);
            }

            this.toastService.showToast("Errore durante la gestione della allerta", IonicColors.RED, 3500, {
              centerText: true,
              hideIcon: true
            });

            return throwError(() => err);
          })
        )
      ),
      finalize(() => {
        if (shouldClearCurrentAlert) {
          this.data.alert = undefined;
        }
        this.manageRunning = false;
      })
    ).subscribe({
      error: () => undefined
    });
  }

  getCurrentAllert() {
    return (this.data.alert && this.sharedSrv.alertsList.find((alert) => alert.id === this.data.alert?.id))
      || this.sharedSrv.sortBySeverity(this.sharedSrv.alertsList.filter((alert) => this.data.devices.some((dev) => alert.deviceId === dev.label)))[0] || undefined;
  }

  private getAlertsToManage(alert: INotificationData): INotificationData[] {
    if (alert.eventType === '08') {
      return [alert];
    }
    const relatedSharedAlerts = this.sharedSrv.alertsList.filter((currAlert) => currAlert.deviceId === alert.deviceId && currAlert.eventType !== '08');
    return relatedSharedAlerts.length ? relatedSharedAlerts : [alert];
  }

  private isAlreadyManagedError(err: unknown): boolean {
    const errorCode = (err as { error?: { error?: string | number } })?.error?.error;
    const errorMessage =
      (err as { error?: { message?: string }, message?: string })?.error?.message ||
      (err as { message?: string })?.message ||
      '';

    return (
      String(errorCode || '') === '15' ||
      /already managed|already handled|gia gestita|già gestita/i.test(String(errorMessage))
    );
  }

  submitDevicesSettings(data: { deviceSettings: ISimpleDevicesSettings | IAdvancedDevicesSettings, deviceSettingsMode: 'simple' | 'advanced' }) {
    if (data.deviceSettingsMode === 'simple') {
      this.data.sensorService.editSimpleDevicesSettings$((this.deviceSettings as ISimpleDevicesSettings)).pipe(
        tap(() => {
          this.toastService.showToast("Temporizzazione salvata con successo");
          this.preferencesMode = !this.preferencesMode;
          this.sharedSrv.unitsTree.forEach((unit) => {
            if (unit.rooms?.find(room => room.id === this.data.roomId)) {
              this.data.sensorService.isSensorSilenced$(unit.rooms.find(room => room.id === this.data.roomId)!.id!).pipe(
                tap((data) => {
                  unit.rooms!.find(room => room.id === this.data.roomId)!.notificationSilencedList = data;
                  unit.rooms!.find(room => room.id === this.data.roomId)!.bedExitDelayMinutes = data.bedExitDelayMinutes;
                })
              ).subscribe();
            }
          });
          this.sharedSrv.unitsTree = this.sharedSrv.unitsTree;
        }),
        catchError((err) => {
          this.toastService.showToast("Errore durante il salvataggio della temporizzazione", IonicColors.NOTIFICATION_HIGH);
          throw err;
        })
      ).subscribe();
    } else {
      this.data.sensorService.editAdvancedDevicesSettings$((this.deviceSettings as IAdvancedDevicesSettings)).pipe(
        tap(() => {
          this.toastService.showToast("Temporizzazione salvata con successo");
          this.preferencesMode = !this.preferencesMode;
          this.sharedSrv.unitsTree.forEach((unit) => {
            if (unit.rooms?.find(room => room.id === this.data.roomId)) {
              this.data.sensorService.isSensorSilenced$(unit.rooms.find(room => room.id === this.data.roomId)!.id!).pipe(
                tap((data) => {
                  unit.rooms!.find(room => room.id === this.data.roomId)!.notificationSilencedList = data;
                  unit.rooms!.find(room => room.id === this.data.roomId)!.bedExitDelayMinutes = data.bedExitDelayMinutes;
                })
              ).subscribe();
            }
          });
          this.sharedSrv.unitsTree = this.sharedSrv.unitsTree;
        }),
        catchError((err) => {
          this.toastService.showToast("Errore durante il salvataggio della temporizzazione", IonicColors.NOTIFICATION_HIGH);
          throw err;
        })
      ).subscribe();
    }
  }
}
